import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import React from 'react';
import { useCart } from '../Contexts/CartContext';
import { specificationMappers } from '../Utils/specificationMappers';

export interface CartDialogProps {
    open: boolean;
    onClose: () => void;
    accentColor: string;
    /** When provided, a "לאישור הזמנה" button is shown in the footer. */
    onCheckout?: () => void;
}

const CartDialog: React.FC<CartDialogProps> = ({ open, onClose, accentColor, onCheckout }) => {
    const { items, removeItem, updateQuantity, totalPrice, totalItems, clearCart } = useCart();

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle fontWeight={700}>
                עגלת קניות ({totalItems})
            </DialogTitle>

            <DialogContent dividers>
                {items.length === 0 ? (
                    <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                        העגלה ריקה
                    </Typography>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {items.map(item => {
                            const name =
                                typeof item.product.name === 'string'
                                    ? item.product.name
                                    : Object.values(item.product.name)[0];
                            const itemBase = (item.product.price ?? 0) * item.quantity;
                            const itemAddons = item.specifications.reduce((s, spec) => {
                                const mapper = specificationMappers[spec.type];
                                return s + (mapper ? mapper.additionalPriceMapper(spec, item.quantity) : 0);
                            }, 0);
                            return (
                                <Paper key={item.cartItemId} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Typography variant="body2" fontWeight={700}>{name}</Typography>
                                        <Typography variant="body2" fontWeight={700} color={accentColor}>
                                            ₪{(itemBase + itemAddons).toFixed(2)}
                                        </Typography>
                                    </Box>

                                    {item.specifications.length > 0 && (
                                        <Box sx={{ mt: 0.5, pl: 1 }}>
                                            {item.specifications.map(spec => {
                                                const mapper = specificationMappers[spec.type];
                                                if (!mapper) return null;
                                                const val = mapper.valueMapper(spec);
                                                return (
                                                    <Typography
                                                        key={spec.id}
                                                        variant="caption"
                                                        color="text.secondary"
                                                        display="block"
                                                    >
                                                        {typeof spec.name === 'string'
                                                            ? spec.name
                                                            : Object.values(spec.name)[0]}: {val}
                                                    </Typography>
                                                );
                                            })}
                                        </Box>
                                    )}

                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            <IconButton
                                                size="small"
                                                disabled={item.quantity <= 1}
                                                onClick={() => updateQuantity(item.cartItemId, item.quantity - 1)}
                                                sx={{ width: 26, height: 26, border: `1px solid ${alpha(accentColor, 0.3)}` }}
                                            >
                                                <RemoveIcon sx={{ fontSize: 13 }} />
                                            </IconButton>
                                            <Typography variant="body2" fontWeight={700} sx={{ minWidth: 24, textAlign: 'center' }}>
                                                {item.quantity}
                                            </Typography>
                                            <IconButton
                                                size="small"
                                                onClick={() => updateQuantity(item.cartItemId, item.quantity + 1)}
                                                sx={{ width: 26, height: 26, border: `1px solid ${alpha(accentColor, 0.3)}` }}
                                            >
                                                <AddIcon sx={{ fontSize: 13 }} />
                                            </IconButton>
                                        </Box>
                                        <Button size="small" color="error" onClick={() => removeItem(item.cartItemId)}>
                                            הסר
                                        </Button>
                                    </Box>
                                </Paper>
                            );
                        })}
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2, flexDirection: 'column', gap: 1 }}>
                {items.length > 0 && (
                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography fontWeight={700}>סה"כ</Typography>
                        <Typography fontWeight={800} color={accentColor}>₪{totalPrice.toFixed(2)}</Typography>
                    </Box>
                )}
                <Box sx={{ width: '100%', display: 'flex', gap: 1 }}>
                    {items.length > 0 && (
                        <Button variant="text" color="error" size="small" onClick={clearCart}>
                            נקה עגלה
                        </Button>
                    )}
                    <Box sx={{ flexGrow: 1 }} />
                    <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2, textTransform: 'none' }}>
                        סגור
                    </Button>
                    {items.length > 0 && onCheckout && (
                        <Button
                            variant="contained"
                            onClick={onCheckout}
                            sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 700,
                                backgroundColor: accentColor,
                                '&:hover': { filter: 'brightness(0.9)', backgroundColor: accentColor },
                            }}
                        >
                            לאישור הזמנה
                        </Button>
                    )}
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default CartDialog;
